/**
 * 节点素材解析 Hook。
 *
 * 用于把节点 custom data 中的 `assetRefs` 转成可直接使用的 CanvasAsset。
 * 当前媒体节点按单素材处理，只读取第一个 assetId；未来多素材节点可以扩展新 Hook。
 */
import { useMemo } from 'react';
import { useSnapshot } from 'valtio/react';
import type { BaseNodeData } from '@/shared/lib/node-registry';
import { useCustomNodeData } from '../../lib/useCustomNodeData';
import type { CanvasAsset } from '../model/canvas-asset-types';
import { canvasAssetsStore } from '../model/canvas-assets-store';
import { readAssetRefsValue } from './node-asset-refs';

export type UseNodeAssetStatus = 'empty' | 'missing' | 'ready';

export interface UseNodeAssetResult {
  asset: CanvasAsset | undefined;
  assetId: string | undefined;
  status: UseNodeAssetStatus;
}

type NodeAssetRefsData = BaseNodeData & {
  assetRefs?: unknown;
};

/**
 * 解析指定节点当前引用的第一个画布素材。
 *
 * @param nodeId 可选节点 ID；不传时读取当前 React Flow 节点上下文中的节点 ID
 * @returns 素材、素材 ID 和状态；状态用于区分无引用、悬空引用和正常素材
 */
export function useNodeAsset(nodeId?: string): UseNodeAssetResult {
  const { snapshot } = useCustomNodeData<NodeAssetRefsData>(nodeId);
  const assets = useSnapshot(canvasAssetsStore.assets);
  const assetRefs = useMemo(
    () => readAssetRefsValue(snapshot.assetRefs),
    [snapshot.assetRefs],
  );
  const assetId = assetRefs[0];
  // useSnapshot(proxyMap) 后按 key 读取，后续只需响应当前 assetId 对应素材的变化。
  const asset = assetId ? assets.get(assetId) as CanvasAsset | undefined : undefined;

  if (!assetId) {
    return {
      asset: undefined,
      assetId: undefined,
      status: 'empty',
    };
  }

  if (!asset) {
    return {
      asset: undefined,
      assetId,
      status: 'missing',
    };
  }

  return {
    asset,
    assetId,
    status: 'ready',
  };
}
